import React, { useState, useEffect } from 'react';
import { BarChart3, Info, ChevronDown, ChevronUp, Trophy, Star, Medal, Target, User } from 'lucide-react';

const LegacyScoreFactors = ({ h2hData = [], finalData = [], yearRange = [2012, 2024] }) => {
  const [factorsData, setFactorsData] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedManager, setExpandedManager] = useState(null);

  const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'];

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to get manager image
  const getManagerImage = (manager) => {
    // Try [manager]main.png first, then fallback to [manager].png
    const mainImage = `/${manager.toLowerCase()}main.png`;
    const fallbackImage = `/${manager.toLowerCase()}.png`;
    
    return {
      main: mainImage,
      fallback: fallbackImage
    };
  };

  // FIXED: Weekly ranking calculation that properly counts ALL instances, not just wins
  const calculateWeeklyPositions = (regSeasonGames, manager = '') => {
    const weeklyPositions = {
      first: 0, twelfth: 0, second: 0, eleventh: 0, third: 0, tenth: 0,
      fourth: 0, ninth: 0, fifth: 0, eighth: 0, sixth: 0, seventh: 0
    };

    regSeasonGames.forEach(game => {
      const winsThisWeek = game['Wins (This Week)'] || 0;
      const lossesThisWeek = game['Losses (This Week)'] || 0;
      
      // IMPORTANT: Validate data integrity
      if (winsThisWeek + lossesThisWeek !== 11) {
        console.warn('Invalid weekly ranking data for', manager, ':', {
          year: game.Year,
          week: game.Week,
          manager: game.Manager1,
          wins: winsThisWeek,
          losses: lossesThisWeek,
          total: winsThisWeek + lossesThisWeek
        });
        return; // Skip invalid records
      }
      
      // Weekly position logic
      if (winsThisWeek === 11) weeklyPositions.first += 1;
      if (winsThisWeek === 0) weeklyPositions.twelfth += 1;
      if (winsThisWeek === 10) weeklyPositions.second += 1;
      if (winsThisWeek === 1) weeklyPositions.eleventh += 1;
      if (winsThisWeek === 9) weeklyPositions.third += 1;
      if (winsThisWeek === 2) weeklyPositions.tenth += 1;
      if (winsThisWeek === 8) weeklyPositions.fourth += 1;
      if (winsThisWeek === 3) weeklyPositions.ninth += 1;
      if (winsThisWeek === 7) weeklyPositions.fifth += 1;
      if (winsThisWeek === 4) weeklyPositions.eighth += 1;
      if (winsThisWeek === 6) weeklyPositions.sixth += 1;
      if (winsThisWeek === 5) weeklyPositions.seventh += 1;
    });

    return weeklyPositions;
  };

  // Calculate all legacy score factors for each manager
  const calculateFactors = () => {
    if (!h2hData.length || !finalData.length) return [];

    const factorsData = [];

    managers.forEach(manager => {
      // Filter data for this manager and year range
      const managerH2H = h2hData.filter(game => 
        game.Manager1 === manager && 
        game.Year >= yearRange[0] && 
        game.Year <= yearRange[1]
      );

      const managerSeasons = finalData.filter(season => 
        season.Owner === manager &&
        season.Year >= yearRange[0] && 
        season.Year <= yearRange[1]
      );

      // Regular season wins/losses
      const regSeasonGames = managerH2H.filter(game => game.Type === 'Regular Season');
      const regWins = regSeasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
      const regLosses = regSeasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);

      // Weekly positions
      const weeklyPositions = calculateWeeklyPositions(regSeasonGames, manager);

      // Playoff wins
      const playoffGames = managerH2H.filter(game => game.Type === 'Playoffs');
      const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);

      // Standings finishes
      const standingsFinishes = Array.from({length: 12}, () => 0);
      managerSeasons.forEach(season => {
        if (season.Standings >= 1 && season.Standings <= 12) {
          standingsFinishes[season.Standings - 1]++;
        }
      });

      // Annual scoring race
      let scoreChamp = 0;
      let scoreLoser = 0;
      
      for (let year = yearRange[0]; year <= yearRange[1]; year++) {
        const seasonData = finalData.filter(season => season.Year === year);
        
        if (seasonData.length > 0) {
          const maxPF = Math.max(...seasonData.map(s => s.PF || 0));
          const minPF = Math.min(...seasonData.map(s => s.PF || 0));
          
          const managerSeason = seasonData.find(s => s.Owner === manager);
          if (managerSeason && managerSeason.PF === maxPF) {
            scoreChamp++;
          }
          
          if (managerSeason && managerSeason.PF === minPF) {
            scoreLoser++;
          }
        }
      }

      factorsData.push({
        manager,
        regWins,
        regLosses,
        weeklyPositions,
        playoffWins,
        standingsFinishes,
        scoreChamp,
        scoreLoser
      });
    });

    // Sort by total legacy score
    return factorsData.sort((a, b) => {
      const scoreA = calculateTotalLegacyScore(a);
      const scoreB = calculateTotalLegacyScore(b);
      return scoreB - scoreA;
    });
  };

  // Calculate total legacy score with proper multipliers
  const calculateTotalLegacyScore = (managerData) => {
    const { regWins, regLosses, weeklyPositions, playoffWins, standingsFinishes, scoreChamp, scoreLoser } = managerData;
    
    const regScore = (regWins * 5) + (regLosses * -5);
    
    const weeklyScore = 
      (weeklyPositions.first * 15) + (weeklyPositions.twelfth * -15) +
      (weeklyPositions.second * 5) + (weeklyPositions.eleventh * -5) +
      (weeklyPositions.third * 4) + (weeklyPositions.tenth * -4) +
      (weeklyPositions.fourth * 3) + (weeklyPositions.ninth * -3) +
      (weeklyPositions.fifth * 2) + (weeklyPositions.eighth * -2) +
      (weeklyPositions.sixth * 1) + (weeklyPositions.seventh * -1);
    
    const standingsScore = 
      (standingsFinishes[0] * 125) +
      (standingsFinishes[1] * 75) +
      (standingsFinishes[2] * 60) +
      (standingsFinishes[3] * 40) +
      (standingsFinishes[4] * 30) +
      (standingsFinishes[5] * 15) +
      (standingsFinishes[6] * -15) +
      (standingsFinishes[7] * -30) +
      (standingsFinishes[8] * -40) +
      (standingsFinishes[9] * -50) +
      (standingsFinishes[10] * -60) +
      (standingsFinishes[11] * -80);
    
    const playoffScore = playoffWins * 10;
    const annualScore = (scoreChamp * 30) + (scoreLoser * -30);
    
    return regScore + weeklyScore + standingsScore + playoffScore + annualScore;
  };

  useEffect(() => {
    if (h2hData.length && finalData.length) {
      const factors = calculateFactors();
      setFactorsData(factors);
    }
  }, [h2hData, finalData, yearRange]);

  // Helper function to get color for cell values
  const getCellColor = (value, isGoodCategory = true) => {
    if (value === 0) return 'bg-gray-100 text-gray-500';
    
    if (isGoodCategory) {
      if (value >= 20) return 'bg-green-700 text-white font-bold';
      if (value >= 15) return 'bg-green-600 text-white font-bold';
      if (value >= 10) return 'bg-green-500 text-white font-semibold';
      if (value >= 5) return 'bg-green-400 text-white';
      return 'bg-green-200 text-green-800';
    } else {
      if (value >= 20) return 'bg-red-700 text-white font-bold';
      if (value >= 15) return 'bg-red-600 text-white font-bold';
      if (value >= 10) return 'bg-red-500 text-white font-semibold';
      if (value >= 5) return 'bg-red-400 text-white';
      return 'bg-red-200 text-red-800';
    }
  };

  // Mobile card component with enhanced design
  const MobileManagerCard = ({ manager, index }) => {
    // This component is no longer used but kept for potential future use
    return null;
  };

  if (!factorsData.length) {
    return (
      <div className="flex items-center justify-center h-48 sm:h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-sm sm:text-base">Loading legacy score factors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`font-bold mb-1 sm:mb-2 ${isMobile ? 'text-xl' : 'text-2xl'}`}>
              Legacy Score Rankings
            </h2>
            <p className={`text-purple-100 ${isMobile ? 'text-sm' : 'text-base'}`}>
              Complete performance breakdown ({yearRange[0]}-{yearRange[1]})
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <BarChart3 className={`text-white ${isMobile ? 'h-6 w-6' : 'h-8 w-8'}`} />
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-3 sm:p-4 mx-3 sm:mx-6 mt-3 sm:mt-6 rounded-lg">
        <div className="flex items-start">
          <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                      <div className={`text-blue-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            <p className="font-semibold mb-2">Legacy Score Components:</p>
            {isMobile ? (
              <div className="space-y-1 text-xs">
                <p><span className="font-medium">Weekly Finishes:</span> 1st (+15), 2nd (+5), 3rd (+4), down to 12th (-15)</p>
                <p><span className="font-medium">Season Record:</span> Win (+5), Loss (-5), Playoff Win (+10)</p>
                <p><span className="font-medium">Final Standings:</span> 1st (+125), 2nd (+75), down to 12th (-80)</p>
                <p><span className="font-medium">Annual Scoring:</span> Champion (+30), Last Place (-30)</p>
              </div>
            ) : (
              <p className="text-sm leading-relaxed">
                <span className="font-medium">Weekly Finishes:</span> 1st (+15), 2nd (+5), 3rd (+4), 4th (+3), 5th (+2), 6th (+1), 7th (-1), 8th (-2), 9th (-3), 10th (-4), 11th (-5), 12th (-15) • 
                <span className="font-medium"> Season Record:</span> Win (+5), Loss (-5) • 
                <span className="font-medium"> Playoffs:</span> Win (+10) • 
                <span className="font-medium"> Final Standings:</span> 1st (+125), 2nd (+75), 3rd (+60), 4th (+40), 5th (+30), 6th (+15), 7th (-15), 8th (-30), 9th (-40), 10th (-50), 11th (-60), 12th (-80) • 
                <span className="font-medium"> Annual Scoring:</span> Champion (+30), Last Place (-30)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-3 sm:mx-6 pb-3 sm:pb-6">
        {isMobile ? (
          // Mobile: Message directing to desktop
          <div className="mt-6 text-center py-8">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
              <BarChart3 className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Desktop View Required
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                The Legacy Score Factors table contains detailed performance breakdowns with many columns. 
                For the best experience, please view this data on a desktop or tablet device.
              </p>
              <div className="mt-4 text-xs text-gray-500">
                💡 Try rotating your device to landscape mode or use a larger screen
              </div>
            </div>
          </div>
        ) : (
          // Desktop: Table Layout (preserved original table with manager images)
          <div className="overflow-x-auto mt-6">
            <table className="w-full border-collapse bg-white rounded-lg shadow-sm">
              <thead>
                {/* Grouping Headers */}
                <tr className="bg-gray-200 text-xs">
                  <th className="px-3 py-2 text-left font-bold text-gray-700 sticky left-0 bg-gray-200 border-r border-gray-300 z-20">
                    Manager
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="2">
                    Regular Season W/L
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="12">
                    Weekly Scoring
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="1">
                    Playoff Wins
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="3">
                    Final Standings: Podium
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="3">
                    Final Standings: Playoffs
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="6">
                    Final Standings: Non-Playoffs
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 border-r border-gray-300" colSpan="2">
                    Annual Scoring Race
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700">
                    Total Score
                  </th>
                </tr>
                
                {/* Column Headers */}
                <tr className="bg-gray-100 text-xs">
                  <th className="px-3 py-2 text-left font-medium text-gray-600 sticky left-0 bg-gray-100 border-r border-gray-200 z-10">
                    Name
                  </th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">Wins</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">Losses</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">1st</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">12th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">2nd</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">11th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">3rd</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">10th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">4th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">9th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">5th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">8th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">6th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">7th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">PO Wins</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">1st</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">2nd</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">3rd</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">4th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">5th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">6th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">7th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">8th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">9th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">10th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">11th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">12th</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600">Champ</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-r border-gray-300">Last</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 bg-yellow-100">Score</th>
                </tr>
              </thead>
              <tbody>
                {factorsData.map((manager, index) => {
                  const totalScore = calculateTotalLegacyScore(manager);
                  const isTopThree = index < 3;
                  const images = getManagerImage(manager.manager);
                  
                  return (
                    <tr key={manager.manager} className={`border-b border-gray-200 hover:bg-gray-50 ${isTopThree ? 'bg-yellow-50' : ''}`}>
                      <td className={`px-3 py-3 font-medium sticky left-0 bg-white border-r border-gray-200 z-10 ${isTopThree ? 'bg-yellow-50 font-bold' : ''}`}>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-300 shadow-sm">
                            <img
                              src={images.main}
                              alt={`${manager.manager} profile`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = images.fallback;
                                e.target.onerror = (fallbackEvent) => {
                                  fallbackEvent.target.style.display = 'none';
                                  fallbackEvent.target.nextSibling.style.display = 'flex';
                                };
                              }}
                            />
                            <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center hidden">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          </div>
                          <span>{manager.manager}</span>
                        </div>
                      </td>
                      
                      {/* Regular Season W/L */}
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.regWins, true)}`}>
                        {manager.regWins}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.regLosses, false)}`}>
                        {manager.regLosses}
                      </td>
                      
                      {/* Weekly Scoring */}
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.first, true)}`}>
                        {manager.weeklyPositions.first}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.twelfth, false)}`}>
                        {manager.weeklyPositions.twelfth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.second, true)}`}>
                        {manager.weeklyPositions.second}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.eleventh, false)}`}>
                        {manager.weeklyPositions.eleventh}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.third, true)}`}>
                        {manager.weeklyPositions.third}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.tenth, false)}`}>
                        {manager.weeklyPositions.tenth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.fourth, true)}`}>
                        {manager.weeklyPositions.fourth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.ninth, false)}`}>
                        {manager.weeklyPositions.ninth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.fifth, true)}`}>
                        {manager.weeklyPositions.fifth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.eighth, false)}`}>
                        {manager.weeklyPositions.eighth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.weeklyPositions.sixth, true)}`}>
                        {manager.weeklyPositions.sixth}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.weeklyPositions.seventh, false)}`}>
                        {manager.weeklyPositions.seventh}
                      </td>
                      
                      {/* Playoff Wins */}
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.playoffWins, true)}`}>
                        {manager.playoffWins}
                      </td>
                      
                      {/* Standings Finishes */}
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[0], true)}`}>
                        {manager.standingsFinishes[0]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[1], true)}`}>
                        {manager.standingsFinishes[1]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.standingsFinishes[2], true)}`}>
                        {manager.standingsFinishes[2]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[3], true)}`}>
                        {manager.standingsFinishes[3]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[4], true)}`}>
                        {manager.standingsFinishes[4]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.standingsFinishes[5], true)}`}>
                        {manager.standingsFinishes[5]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[6], false)}`}>
                        {manager.standingsFinishes[6]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[7], false)}`}>
                        {manager.standingsFinishes[7]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[8], false)}`}>
                        {manager.standingsFinishes[8]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[9], false)}`}>
                        {manager.standingsFinishes[9]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.standingsFinishes[10], false)}`}>
                        {manager.standingsFinishes[10]}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.standingsFinishes[11], false)}`}>
                        {manager.standingsFinishes[11]}
                      </td>
                      
                      {/* Annual Scoring */}
                      <td className={`px-2 py-3 text-center text-sm ${getCellColor(manager.scoreChamp, true)}`}>
                        {manager.scoreChamp}
                      </td>
                      <td className={`px-2 py-3 text-center text-sm border-r border-gray-300 ${getCellColor(manager.scoreLoser, false)}`}>
                        {manager.scoreLoser}
                      </td>
                      
                      {/* Total Score */}
                      <td className={`px-2 py-3 text-center text-sm font-bold bg-yellow-100 ${totalScore >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {totalScore}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacyScoreFactors;